/* ============================================================
   lab-contact.js — Lab Effects Contact page logic (theme asset)
   - Two forms, one page, each its own trackable "view": switching the
     chooser updates the URL (/contact ↔ /business-inquiry) via the
     History API and fires a GA4 virtual page_view + Microsoft Clarity
     tag, so each form is a distinct funnel/drop-off — no page reload.
   - Deep-linkable: /business-inquiry (or ?form=business / #business)
     opens straight onto the business form.
   - Engagement: form_start, form_field_complete, form_submit, generate_lead.
   - Conditional reveals are driven by [data-reveal="<targetId>"] on the
     triggering input, so they keep working even when field NAMES change to
     Shopify contact[...] keys or Salesforce 00N… ids.
   BOTH forms now post through Shopify ({% form 'contact' %}) — the business
   form used to POST cross-origin to Salesforce. Two consequences handled here:
     • Checkbox groups can't share one name without losing all but the last
       value, so each [data-serialize-to] group is flattened into a single
       hidden contact[...] input on submit.
     • Shopify marks BOTH forms posted_successfully? on a successful post (it
       is page-level state, and both are form_type=contact), so we stash which
       form was actually submitted in sessionStorage and, after the reload,
       keep only that success panel and reopen its tab.
   Both submit normally on a live store — the no-backend branch below only runs
   in local preview.
   Virtual paths + form ids are read from each <form>'s data-* attributes.
   ============================================================ */
(function () {
  "use strict";

  function ga() { if (typeof window.gtag === "function") window.gtag.apply(null, arguments); }
  function clarity() { if (typeof window.clarity === "function") window.clarity.apply(null, arguments); }
  function track(event, params) {
    ga("event", event, params || {});
    if (params && params.form_id) clarity("set", "form", params.form_id);
    clarity("event", event);
  }
  function virtualPageView(path, title) {
    ga("event", "page_view", { page_path: path, page_location: location.origin + path, page_title: title });
    clarity("set", "page", path);
  }

  // build form registry from the DOM (paths/titles come from data-* attrs)
  // The canonical, RESOLVABLE path of this page, captured before any history
  // rewrite. Used for the address bar; def.path stays analytics-only.
  var BASE_PATH = location.pathname;

  var FORMS = {};
  Array.prototype.forEach.call(document.querySelectorAll(".cx-form"), function (f) {
    var id = f.getAttribute("data-form-id");
    if (!id) return;
    FORMS[id] = {
      panel: f.closest(".cx-panel") ? f.closest(".cx-panel").id : null,
      path: f.getAttribute("data-track-path") || ("/" + id),
      title: f.getAttribute("data-track-title") || ("Contact — " + id)
    };
  });

  var SUBMIT_KEY = "lecx-submitted-form";

  // Flatten each checkbox group into one hidden contact[...] field so it lands
  // in the notification email as "Products of Interest: A, B, C".
  function serializeGroups(form) {
    Array.prototype.forEach.call(form.querySelectorAll("[data-serialize-to]"), function (group) {
      var name = "contact[" + group.getAttribute("data-serialize-to") + "]";
      var vals = Array.prototype.slice
        .call(group.querySelectorAll('input[type="checkbox"]:checked'))
        .map(function (i) { return i.value; });
      var hidden = form.querySelector('input[type="hidden"][name="' + name + '"]');
      if (!hidden) {
        hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = name;
        form.appendChild(hidden);
      }
      hidden.value = vals.join(", ");
    });
  }

  var tabs = Array.prototype.slice.call(document.querySelectorAll(".cx-tab"));
  var started = {};

  function activeFromLocation() {
    var p = location.pathname.toLowerCase();
    var q = new URLSearchParams(location.search);
    var h = (location.hash || "").replace("#", "").toLowerCase();
    if (p.indexOf("business-inquiry") > -1) return "business";
    if (q.get("form") === "business" || h === "business") return "business";
    return "general";
  }

  function showForm(key, opts) {
    opts = opts || {};
    var def = FORMS[key];
    if (!def) return;
    tabs.forEach(function (t) {
      var on = t.getAttribute("data-form") === key;
      t.classList.toggle("is-active", on);
      t.setAttribute("aria-selected", on ? "true" : "false");
    });
    Object.keys(FORMS).forEach(function (k) {
      var panel = document.getElementById(FORMS[k].panel);
      if (panel) panel.hidden = k !== key;
    });
    // NEVER put def.path in the address bar. It is a GA4 VIRTUAL path (the
    // theme setting is literally labelled "GA4 virtual path") — "/business-inquiry"
    // was a real page on Webflow but 404s on Shopify. Writing it into history meant
    // Shopify's post-submit redirect returned the customer to a dead URL, so every
    // business-inquiry submission ended on "Page not found". Analytics still gets
    // def.path via virtualPageView() below; the address bar gets a real URL.
    var realUrl = BASE_PATH + (key === "general" ? "" : "?form=" + encodeURIComponent(key));
    try {
      if (!opts.replace) history.pushState({ form: key }, "", realUrl);
      else history.replaceState({ form: key }, "", realUrl);
    } catch (err) { /* keep working if History API is blocked */ }
    virtualPageView(def.path, def.title);
    track("form_view", { form_id: key });
    if (opts.focus) {
      var first = document.querySelector("#" + def.panel + " input, #" + def.panel + " textarea, #" + def.panel + " select");
      if (first) first.focus();
    }
  }

  tabs.forEach(function (t) {
    t.addEventListener("click", function () { showForm(t.getAttribute("data-form"), { focus: false }); });
  });
  window.addEventListener("popstate", function () { showForm(activeFromLocation(), { replace: true }); });

  /* ---------- country fallback (only if Liquid didn't render options) ---------- */
  var sel = document.getElementById("b-country");
  if (sel && sel.querySelectorAll("option").length <= 1) {
    ["United States","Canada","United Kingdom","Australia","Germany","Netherlands","Spain","France","Italy","Portugal","Switzerland","Austria","Belgium","Denmark","Sweden","Norway","Finland","Ireland","Poland","Czech Republic","Greece","Mexico","Brazil","Argentina","Chile","Colombia","Costa Rica","Uruguay","South Africa","Israel","Japan","South Korea","Thailand","New Zealand","India","United Arab Emirates","Other"].forEach(function (c) {
      var o = document.createElement("option"); o.value = c; o.textContent = c; sel.appendChild(o);
    });
  }

  /* ---------- conditional reveals (data-reveal driven) ---------- */
  function reveal(el, open) { if (el) el.classList.toggle("open", !!open); }
  function focusSoon(id) { setTimeout(function () { var f = document.getElementById(id); if (f) f.focus(); }, 80); }

  // Re-evaluate every [data-reveal] trigger inside a form. Each trigger opens
  // the panel named by its data-reveal value while it is checked.
  function syncReveals(form) {
    Array.prototype.forEach.call(form.querySelectorAll("[data-reveal]"), function (el) {
      var target = document.getElementById(el.getAttribute("data-reveal"));
      var open = !!el.checked;
      reveal(target, open);
      // a revealed business-name field becomes required; otherwise default it to "NA"
      var inner = target ? target.querySelector("[data-company]") : null;
      if (inner) {
        inner.required = open;
        if (!open) inner.value = "NA"; else if (inner.value === "NA") inner.value = "";
      }
      if (open && el.getAttribute("data-focus")) focusSoon(el.getAttribute("data-focus"));
    });
  }

  /* ---------- engagement tracking + validation + reveals per form ---------- */
  function wireForm(key) {
    var form = document.getElementById(key + "-form");
    if (!form) return;
    var formId = form.getAttribute("data-form-id");

    form.addEventListener("change", function () { syncReveals(form); });

    form.addEventListener("focusin", function () {
      if (started[formId]) return;
      started[formId] = true;
      track("form_start", { form_id: formId });
    });
    form.addEventListener("focusout", function (e) {
      var el = e.target;
      if (!el.name || !("value" in el)) return;
      if (el.value && el.value.trim()) track("form_field_complete", { form_id: formId, field: el.name });
      el.classList.remove("err");
    });
    form.addEventListener("submit", function (e) {
      var invalid = [];
      Array.prototype.forEach.call(form.querySelectorAll("[required]"), function (el) {
        if (el.offsetParent === null && el.type !== "hidden") return; // skip hidden (collapsed) required fields
        var ok = el.type === "radio" ? !!form.querySelector('[name="' + el.name + '"]:checked') : !!(el.value && el.value.trim());
        if (!ok) { el.classList.add("err"); invalid.push(el); }
      });
      if (invalid.length) {
        e.preventDefault();
        invalid[0].focus();
        track("form_error", { form_id: formId, missing: invalid.length });
        return;
      }
      serializeGroups(form);
      try { sessionStorage.setItem(SUBMIT_KEY, formId); } catch (err) {}
      track("form_submit", { form_id: formId });
      if (formId === "business") track("generate_lead", { form_id: formId, value: 1, currency: "USD" });
      // No-backend preview fallback (live store POSTs normally — this only runs locally):
      if (location.protocol === "file:" || /localhost|127\.0\.0\.1/i.test(location.host)) {
        e.preventDefault();
        var panel = document.getElementById(FORMS[formId].panel);
        var success = document.getElementById("success-" + formId);
        var body = form.querySelector(".cx-form-body");
        if (success) {
          if (body) body.style.display = "none"; else form.style.display = "none";
          success.classList.add("show");
          if (panel) window.scrollTo({ top: panel.getBoundingClientRect().top + window.scrollY - 90, behavior: "smooth" });
        }
      }
    });

    syncReveals(form); // set initial state (e.g. Authorized defaults, restored values)
  }
  wireForm("general");
  wireForm("business");

  showForm(activeFromLocation(), { replace: true });

  // Shopify reloads after a successful contact post and flags BOTH forms as
  // posted. Show only the one that was actually submitted, and open its tab.
  (function restoreAfterSubmit() {
    var shown = document.querySelectorAll(".cx-success.show");
    if (!shown.length) return;
    var which = null;
    try {
      which = sessionStorage.getItem(SUBMIT_KEY);
      sessionStorage.removeItem(SUBMIT_KEY);
    } catch (err) {}
    if (!which || !FORMS[which]) return;
    Array.prototype.forEach.call(shown, function (el) {
      var f = el.closest(".cx-form");
      if (f && f.getAttribute("data-form-id") !== which) el.classList.remove("show");
    });
    showForm(which, { replace: true });
  })();
})();
